import { useState } from "react";
import { toast } from "sonner";
import { lerXmlDeNota, type DadosDaNota, type ResultadoDaImportacao } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useArquivoDaNota } from "../use-arquivo-da-nota";

/** pronta: entra no lote. As outras ficam de fora, com o motivo na linha. */
export type Situacao = "pronta" | "jaRegistrada" | "repetida" | "emitida" | "naoNota" | "incompleta";
export type Despesa = "nova" | "ligar" | "nenhuma";

export type Linha = {
  id: string;
  arquivo: File;
  dados: DadosDaNota | null;
  situacao: Situacao;
  fornecedor: { id: string; nome: string } | null;
  despesaExistente: { id: string; descricao: string } | null;
  mesFechado: boolean;
  incluir: boolean;
  criarFornecedor: boolean;
  categoriaId: string;
  despesa: Despesa;
  resultado?: { resultado: ResultadoDaImportacao | "envio"; mensagem?: string };
};

const soDigitos = (v: string | null | undefined) => (v ?? "").replace(/\D/g, "");
const LOTE = 50;

/** Lê os XMLs no navegador, pergunta ao servidor o que cada nota encontra e grava o lote. */
export function useImportacaoDeNotas(categoriaPadrao: string) {
  const utils = trpc.useUtils();
  const { enviar } = useArquivoDaNota();
  const previa = trpc.financeiro.notas.previaDaImportacao.useMutation();
  const importar = trpc.financeiro.notas.importar.useMutation();
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [lendo, setLendo] = useState(false);
  const [progresso, setProgresso] = useState<{ feitas: number; total: number } | null>(null);

  async function adicionar(arquivos: File[]) {
    const xmls = arquivos.filter((f) => f.name.toLowerCase().endsWith(".xml"));
    if (!xmls.length) return;
    setLendo(true);
    try {
      const lidas = await Promise.all(xmls.map(async (arquivo) => ({ arquivo, dados: lerXmlDeNota(await arquivo.text()) })));
      const completas = lidas.filter((l) => l.dados?.numero && l.dados.valorCents && l.dados.emitidaEm);
      const resposta = completas.length
        ? await previa.mutateAsync({
            itens: completas.map(({ dados: d }) => ({
              chaveDeAcesso: d!.chaveDeAcesso,
              numero: d!.numero!,
              documento: d!.emitente.documento,
              valorCents: d!.valorCents!,
              emitidaEm: d!.emitidaEm!,
            })),
          })
        : { documentoDoLaboratorio: null, itens: [] };
      // Mesma chave duas vezes no que foi escolhido (inclusive no que já estava na lista).
      const chaves = new Set(linhas.map((l) => l.dados?.chaveDeAcesso).filter(Boolean));
      const novas: Linha[] = lidas.map(({ arquivo, dados }) => {
        const i = completas.findIndex((c) => c.arquivo === arquivo);
        const p = i >= 0 ? resposta.itens[i] : null;
        const chave = dados?.chaveDeAcesso;
        const emitidaPorNos = !!resposta.documentoDoLaboratorio && soDigitos(dados?.emitente.documento) === resposta.documentoDoLaboratorio;
        const situacao: Situacao = !dados
          ? "naoNota"
          : !p
            ? "incompleta"
            : emitidaPorNos
              ? "emitida"
              : p.jaRegistrada
                ? "jaRegistrada"
                : chave && chaves.has(chave)
                  ? "repetida"
                  : "pronta";
        if (chave) chaves.add(chave);
        return {
          id: crypto.randomUUID(),
          arquivo,
          dados,
          situacao,
          fornecedor: p?.fornecedor ?? null,
          despesaExistente: p?.despesaExistente ?? null,
          mesFechado: p?.mesFechado ?? false,
          incluir: situacao === "pronta",
          criarFornecedor: !p?.fornecedor && !!dados?.emitente.nome,
          categoriaId: categoriaPadrao,
          despesa: p?.despesaExistente ? "ligar" : "nova",
        };
      });
      setLinhas((atuais) => [...atuais, ...novas]);
    } finally {
      setLendo(false);
    }
  }

  const mudar = (id: string, parcial: Partial<Linha>) => setLinhas((ls) => ls.map((l) => (l.id === id ? { ...l, ...parcial } : l)));
  const aplicarCategoriaATodas = (categoriaId: string) => setLinhas((ls) => ls.map((l) => ({ ...l, categoriaId })));
  // Os XMLs podem chegar antes das categorias: linha sem escolha usa a padrão de agora.
  const categoriaDe = (l: Linha) => l.categoriaId || categoriaPadrao;

  async function gravar() {
    const alvo = linhas.filter((l) => l.incluir && l.situacao === "pronta" && !l.resultado);
    setProgresso({ feitas: 0, total: alvo.length });
    try {
      await enviarLote(alvo);
    } catch (e) {
      const mensagem = (e as Error).message;
      setLinhas((ls) => ls.map((l) => (alvo.some((a) => a.id === l.id) && !l.resultado ? { ...l, resultado: { resultado: "erro", mensagem } } : l)));
      toast.error(mensagem);
    } finally {
      setProgresso({ feitas: alvo.length, total: alvo.length });
      utils.financeiro.invalidate();
      utils.partner.invalidate();
    }
  }

  async function enviarLote(alvo: Linha[]) {
    // Sobe os XMLs de 3 em 3; o que não sobe sai do lote com o motivo.
    const enviados: { linha: Linha; fileId: string }[] = [];
    for (let i = 0; i < alvo.length; i += 3) {
      const grupo = alvo.slice(i, i + 3);
      const res = await Promise.allSettled(grupo.map((l) => enviar(l.arquivo)));
      res.forEach((r, j) => {
        if (r.status === "fulfilled") enviados.push({ linha: grupo[j]!, fileId: r.value.enviado.fileId });
        else mudar(grupo[j]!.id, { resultado: { resultado: "envio", mensagem: (r.reason as Error).message } });
      });
    }
    let feitas = 0;
    for (let i = 0; i < enviados.length; i += LOTE) {
      const parte = enviados.slice(i, i + LOTE);
      const resultados = await importar.mutateAsync({
        notas: parte.map(({ linha: l, fileId }) => ({
          numero: l.dados!.numero!,
          serie: l.dados!.serie,
          chaveDeAcesso: l.dados!.chaveDeAcesso,
          emitidaEm: l.dados!.emitidaEm!,
          valorCents: l.dados!.valorCents!,
          fornecedor: { nome: (l.dados!.emitente.nome || "Fornecedor").slice(0, 160), documento: l.dados!.emitente.documento },
          criarFornecedor: l.criarFornecedor,
          categoriaId: categoriaDe(l) || null,
          despesa: l.despesa,
          lancamentoId: l.despesa === "ligar" ? (l.despesaExistente?.id ?? null) : null,
          fileId,
        })),
      });
      parte.forEach(({ linha }, j) => mudar(linha.id, { resultado: resultados[j] }));
      feitas += parte.length;
      setProgresso({ feitas, total: alvo.length });
    }
  }

  return { linhas, adicionar, mudar, aplicarCategoriaATodas, categoriaDe, gravar, lendo, gravando: progresso !== null && progresso.feitas < progresso.total, progresso, limpar: () => (setLinhas([]), setProgresso(null)) };
}
